// Work kit — issue BODY controller (wave 4, Tracks B/E/F/G). Mounted on the
// issue body root alongside `ui--work-issue-url-state`; every section binds
// its actions HERE (one writer per root — sections never double-mount it).
//
// Owns:
//   - `[` / `]` prev/next navigation with the flow form-field guard (pinned:
//     no navigation while typing in inputs/textarea/select/contenteditable),
//   - "…" menu action events (`ui--work:action`, host data only),
//   - worklog add (duration grammar parsed client-side → seconds; started_on
//     stays DATE-ONLY, P16) + ack reset,
//   - inline subtask create (optimistic row stamped with a per-op `op_token`
//     — the comments W9 discipline; the echoed ack clears/reverts exactly
//     that op's row, tokenless legacy acks keep the coarse sweep, exactly
//     one toast on failure),
//   - attachments: submit emits `ui--work:attachment-add` carrying the
//     PRE-BUILT injected upload URL (the kit never constructs a URL and
//     performs no upload); remove emits `ui--work:attachment-remove-request`
//     and a row leaves the list ONLY on the host-dispatched
//     `ui--work:attachment-remove` consumption event — no optimistic removal,
//   - delivery link/create affordances emitting `ui--work:delivery-link` /
//     `ui--work:delivery-link-create` (the kit opens no dialogs),
//   - navigate events from the prev/next affordances and depth-ceiling
//     subtask rows.
//
// Subject key pre-seed (W8F B1 — ledger ESCALATE :79/:119, coverage-audit
// amendment 33): every payload emitter keys the subject record with the
// host-injected `subject-key` value (default "issue_id" — byte-identical to
// the W4 contract). A host mounting this body over a database record panel
// passes `subject_key: "record_id"` (mirroring the W9B comments seam) so no
// payload-key lie is emitted. The ack direction is the SAME contract: hosts
// ack with the SAME injected key (one writer per key — the URL law).

// The kit performs no HTTP (W0 §5): every mutation bubbles a `ui--work:*`
// CustomEvent from this root; the host translates and acks.

import { Controller } from "@hotwired/stimulus"
import { clearToasts, showToast, toastRegionFor } from "../../../lib/work/mutation_seam"

const WORKLOG_ACK_EVENT = "ui--work:worklog-ack"
const SUBTASK_ACK_EVENT = "ui--work:subtask-ack"
const NAVIGATE_EVENT = "ui--work:navigate"
const ACTION_EVENT = "ui--work:action"
const WORKLOG_ADD_EVENT = "ui--work:worklog-add"
const SUBTASK_CREATE_EVENT = "ui--work:subtask-create"
const ATTACHMENT_ADD_EVENT = "ui--work:attachment-add"
const ATTACHMENT_ACK_EVENT = "ui--work:attachment-ack"
const ATTACHMENT_REMOVE_REQUEST_EVENT = "ui--work:attachment-remove-request"
const ATTACHMENT_REMOVE_EVENT = "ui--work:attachment-remove"
const DELIVERY_LINK_EVENT = "ui--work:delivery-link"
const DELIVERY_LINK_CREATE_EVENT = "ui--work:delivery-link-create"

// Flow parity: typing in a form field must never trigger body-level keys.
const FORM_FIELD_SELECTOR = "input, textarea, select, button, [contenteditable]"

// One combined scan drives duration extraction and the junk guard in
// parseDuration: fractional amounts with dot OR comma decimals (".5h",
// ",5m" included) — but a [.,] DIRECTLY AFTER a unit word (the second
// alternative's lookbehind) is a SEPARATOR, never a decimal start:
// "1h,30m" reads 1h + 30m = 5400 (the comma is prose; the digits still
// match the digit-prefixed alternative), not the silent 0.3m=18s misread.
// At string start or after whitespace the fraction is real (".5h", ",5m").
// Unit words d/day(s), h/our(s), m/in(ute)(s) — the semantic mirror of the
// Ruby WorklogComponent::DURATION_UNIT_RE. Sum-all: every matched span
// counts ("2m 2m" → 240). The two grammars must never disagree. NOTE the
// anchor spelling: JS has no \A — inside a lookbehind it degrades to a
// plain letter escape matching "A" (vitest caught ".5h" → 18000 exactly so)
// — so the JS side spells string start `^` (no /m flag ⇒ string start
// only); the Ruby mirror spells it \A. Same language, same semantics.
const DURATION_UNIT_RE = /(\d+(?:[.,]\d+)?|(?<=^|\s)[.,]\d+)\s*(d(?:ay)?s?|h(?:our)?s?|m(?:in(?:ute)?)?s?)/gi

// Per-op token generator — the comments W9 idiom (module-local counter +
// crypto UUID, Math.random fallback). One token per optimistic subtask row:
// stamped as `data-work-subtask-op` and emitted as `op_token`, so the host's
// echoed ack reverts exactly one row instead of sweeping every concurrent
// optimistic submit.
let opCounter = 0
function newOpToken() {
  opCounter += 1
  if (globalThis.crypto?.randomUUID) {
    return `work-subtask-${opCounter}-${globalThis.crypto.randomUUID()}`
  }
  return `work-subtask-${opCounter}-${Math.random().toString(36).slice(2)}`
}

export default class extends Controller {
  static values = {
    issueId: String,
    issueKey: String,
    prevUrl: String,
    nextUrl: String,
    subjectKey: { type: String, default: "issue_id" },
  }

  static targets = ["toastRegion", "worklogForm", "subtaskForm", "attachmentForm"]

  connect() {
    this._teardown()
    this._onWorklogAck = this._handleWorklogAck.bind(this)
    this._onSubtaskAck = this._handleSubtaskAck.bind(this)
    this._onAttachmentAck = this._handleAttachmentAck.bind(this)
    this._onAttachmentRemove = this._handleAttachmentRemove.bind(this)
    document.addEventListener(WORKLOG_ACK_EVENT, this._onWorklogAck)
    document.addEventListener(SUBTASK_ACK_EVENT, this._onSubtaskAck)
    document.addEventListener(ATTACHMENT_ACK_EVENT, this._onAttachmentAck)
    document.addEventListener(ATTACHMENT_REMOVE_EVENT, this._onAttachmentRemove)
  }

  disconnect() {
    this._teardown()
  }

  // Teardown-first re-bind (dashboard_grid idiom, ghost-closure family): a
  // same-instance double-connect (Turbo cache restore racing a morph) must
  // not stack a SECOND document closure alongside the first — the orphaned
  // ghost keeps the old instance alive and re-handles every ack (double
  // toasts, double row sweeps). Removing first is a no-op when nothing is
  // bound, so exactly one live closure per event survives any connect order.
  _teardown() {
    document.removeEventListener(WORKLOG_ACK_EVENT, this._onWorklogAck)
    document.removeEventListener(SUBTASK_ACK_EVENT, this._onSubtaskAck)
    document.removeEventListener(ATTACHMENT_ACK_EVENT, this._onAttachmentAck)
    document.removeEventListener(ATTACHMENT_REMOVE_EVENT, this._onAttachmentRemove)
    this._onWorklogAck = null
    this._onSubtaskAck = null
    this._onAttachmentAck = null
    this._onAttachmentRemove = null
  }

  // --- Public data API (spec Track G deliverable 2) -------------------------
  //
  // Open a nested issue drawer (issue-on-issue at stack_level 1) by drawer
  // id — the same path the subtask rows' ui--drawer-opener buttons drive
  // (CONEK-6530): parent scoping via openedFrom, then the drawer's own
  // controller show(). Unknown ids are a no-op. Hosts call this to open a
  // nested drawer from glue code that has no row in the subtask list.

  openNested(drawerId) {
    if (typeof drawerId !== "string" || !drawerId) return
    const drawerEl = document.getElementById(drawerId)
    if (!drawerEl) return

    const parent = this.element.closest('[data-controller~="ui--drawer"]')
    if (parent?.id) {
      drawerEl.dataset.openedFrom = parent.id
    } else {
      delete drawerEl.dataset.openedFrom
    }

    const controller = this.application.getControllerForElementAndIdentifier(drawerEl, "ui--drawer")
    controller?.show()
  }

  // --- keyboard contract (pin B7): [ prev, ] next ---

  keydown(event) {
    if (event.key !== "[" && event.key !== "]") return
    // Form-field guard — flow parity: skip when focus sits in a form field.
    const active = document.activeElement
    if (active && (active.matches?.(FORM_FIELD_SELECTOR) || active.isContentEditable)) return
    if (event.metaKey || event.ctrlKey || event.altKey) return

    event.preventDefault()
    if (event.key === "[") {
      this.navigatePrev()
    } else {
      this.navigateNext()
    }
  }

  navigatePrev() {
    if (!this.prevUrlValue) return
    this._emit(NAVIGATE_EVENT, { [this.subjectKeyValue]: this.issueIdValue, direction: "prev" })
  }

  navigateNext() {
    if (!this.nextUrlValue) return
    this._emit(NAVIGATE_EVENT, { [this.subjectKeyValue]: this.issueIdValue, direction: "next" })
  }

  // Depth-ceiling subtask rows (stack >= 2): emit an open-navigation instead
  // of nesting a third drawer (pinned drawer depth ceiling). The navigated
  // ROW's id rides the subject key (the record being opened), not the
  // mounted body's own id.
  navigateToIssue(event) {
    const issueId = event.currentTarget.getAttribute("data-work-navigate-issue-id")
    if (!issueId) return
    this._emit(NAVIGATE_EVENT, { [this.subjectKeyValue]: issueId, direction: "open" })
  }

  // --- "…" menu (host-injected actions) ---

  emitAction(event) {
    const actionId = event.currentTarget.getAttribute("data-work-action-id") || ""
    this._emit(ACTION_EVENT, { [this.subjectKeyValue]: this.issueIdValue, action_id: actionId })
  }

  // --- worklog add (pin F4: client-side duration → seconds; P16 date-only) ---

  submitWorklog(event) {
    event.preventDefault()
    const form = event.currentTarget
    const durationRaw = form.querySelector('[name="duration"]')?.value?.trim() || ""
    const startedOn = form.querySelector('[name="started_on"]')?.value?.trim() || ""
    const comment = form.querySelector('[name="comment"]')?.value?.trim() || ""

    const seconds = this.parseDuration(durationRaw)
    if (seconds === null) {
      showToast(toastRegionFor(this.element), this._worklogErrorCopy(), "error")
      return
    }

    this._emit(WORKLOG_ADD_EVENT, {
      [this.subjectKeyValue]: this.issueIdValue,
      seconds: seconds,
      started_on: startedOn, // date-only string ("YYYY-MM-DD") — never a timestamp
      ...(comment ? { comment: comment } : {}),
    })
    showToast(toastRegionFor(this.element), "", "loading")
  }

  // Duration grammar: "1h 30m" / "90m" / "2h" / "1d" / fractional units
  // ("1.5h" — dot or comma decimal, ".5h" / ",5m") — and the clock form
  // "1:30" (H:MM) delegates to the SHARED Ui::TimeValueParsing semantics
  // via the Ruby-rendered pattern; client-side we accept the same shape.
  parseDuration(raw) {
    // Normalize every whitespace kind to ASCII space first: the JS \s class
    // is Unicode-aware while Ruby \s is not, so "1h<NBSP>30m" would parse
    // differently per seam. The classes are made co-extensive: \s covers
    // White_Space + U+FEFF but not U+0085, so U+0085 is added; the Ruby side
    // mirrors by adding U+FEFF to its [[:space:]].
    const value = raw.trim().replace(/[\s\u0085]+/g, " ")
    if (!value) return null
    // Clock form "H:MM" — the same ranges the Ruby side enforces via the
    // shared Ui::TimeValueParsing (hour 0-23, minute 0-59); the two grammars
    // must never disagree (fresh-eyes minor: \d{1,3} accepted "25:00").
    // "0:00" is rejected like any zero duration.
    const clock = value.match(/^(\d{1,2}):(\d{2})$/)
    if (clock) {
      const hours = parseInt(clock[1], 10)
      const minutes = parseInt(clock[2], 10)
      if (hours > 23 || minutes >= 60) return null
      const seconds = hours * 3600 + minutes * 60
      return seconds > 0 ? seconds : null
    }
    const matches = [...value.matchAll(DURATION_UNIT_RE)]
    if (matches.length === 0) return null
    // Junk guard: any digit outside a matched span ("1.5.5h" would otherwise
    // scan-past and read as 5.5h) makes the input garbage → null, not a
    // silently wrong total. matchAll and replace share leftmost-
    // non-overlapping semantics, so the spans stripped here are exactly the
    // spans matched above.
    if (/\d/.test(value.replace(DURATION_UNIT_RE, ""))) return null
    let total = 0
    for (const m of matches) {
      const amount = parseFloat(m[1].replace(",", "."))
      const unit = m[2][0].toLowerCase()
      total += amount * (unit === "d" ? 86400 : unit === "h" ? 3600 : 60)
    }
    const seconds = Math.round(total)
    return seconds > 0 ? seconds : null
  }

  // --- inline subtask create (pin G4: exactly one event per submit) ---

  submitSubtask(event) {
    event.preventDefault()
    const form = event.currentTarget
    const input = form.querySelector('[name="summary"]')
    const summary = input?.value?.trim() || ""
    if (!summary) return

    // Per-op token (the comments W9 discipline): the row is stamped and the
    // token rides the intent event, so the ack reverts exactly one op.
    const opToken = newOpToken()

    // Optimistic row: plain text only (textContent — never innerHTML).
    // The list carries `data-work-subtask-list` so the FIRST subtask ever
    // created (empty list) still gets its optimistic row.
    const list = this.element.querySelector("[data-work-subtask-list]")
    if (list) {
      const li = document.createElement("li")
      li.setAttribute("data-work-subtask-optimistic", "")
      li.setAttribute("data-work-subtask-op", opToken)
      li.textContent = summary
      list.prepend(li)
    }
    if (input) input.value = ""

    this._emit(SUBTASK_CREATE_EVENT, {
      [this.subjectKeyValue]: this.issueIdValue,
      summary: summary,
      op_token: opToken,
    })
  }

  // --- attachments (Track F deliverable 3) ---

  // The upload form NEVER posts: the event carries the PRE-BUILT injected
  // upload URL and the host glue island performs the upload itself.
  submitAttachment(event) {
    event.preventDefault()
    const form = event.currentTarget
    const uploadUrl = form.getAttribute("data-work-upload-url") || ""
    this._emit(ATTACHMENT_ADD_EVENT, { [this.subjectKeyValue]: this.issueIdValue, upload_url: uploadUrl })
    showToast(toastRegionFor(this.element), "", "loading")
  }

  // Remove request from a FilePreview card trash. The card's delete action
  // is `${controller_scope}#removeFile` (Ui::FilePreviewComponent contract),
  // so with controller_scope "ui--work-issue" this method owns it. Emits the
  // request ONLY — the row leaves the list on the host-dispatched
  // consumption event below (no optimistic removal). Silent by design: no
  // toast, no rollback.
  removeFile(event) {
    const row = event.currentTarget.closest("[data-work-attachment-id]")
    const attachmentId = row ? row.getAttribute("data-work-attachment-id") : ""
    if (!attachmentId) return
    this._emit(ATTACHMENT_REMOVE_REQUEST_EVENT, { [this.subjectKeyValue]: this.issueIdValue, attachment_id: attachmentId })
  }

  emitDeliveryLink(event) {
    const workItemRef = event.currentTarget.getAttribute("data-work-delivery-ref") || ""
    this._emit(DELIVERY_LINK_EVENT, { [this.subjectKeyValue]: this.issueIdValue, work_item_ref: workItemRef })
  }

  emitDeliveryLinkCreate(event) {
    const workItemDef = event.currentTarget.getAttribute("data-work-delivery-def") || ""
    this._emit(DELIVERY_LINK_CREATE_EVENT, { [this.subjectKeyValue]: this.issueIdValue, work_item_def: workItemDef })
  }

  // --- internals ---

  _handleWorklogAck(event) {
    const detail = event.detail || {}
    if (detail[this.subjectKeyValue] !== this.issueIdValue) return
    const region = toastRegionFor(this.element)
    if (detail.ok) {
      clearToasts(region)
      const form = this.hasWorklogFormTarget ? this.worklogFormTarget : null
      if (form) form.reset()
    } else {
      showToast(region, this._worklogErrorCopy(), "error")
    }
  }

  _handleSubtaskAck(event) {
    const detail = event.detail || {}
    if (detail[this.subjectKeyValue] !== this.issueIdValue) return
    const region = toastRegionFor(this.element)
    // The host echoes the submit's per-op `op_token` back on the ack (it rides
    // the event detail verbatim). A token scopes the row removal to exactly
    // one op — concurrent optimistic submits for this issue stand (their own
    // acks settle them), and an ok ack for one op no longer drops rows whose
    // hosts ack without a re-render.
    const token = typeof detail.op_token === "string" && detail.op_token ? detail.op_token : null
    const row = token ? this._optimisticRowByOp(token) : null

    if (detail.ok) {
      // The host re-renders the list with the persisted row; drop OURS only.
      if (row) row.remove()
      else if (!token) {
        // Tokenless (legacy W4) ok ack — it cannot be attributed to an op,
        // so it keeps the coarse sweep (the host's re-render supersedes
        // every pending row). A tokened ack matching nothing is stale and
        // stands down (its op has its own ack coming).
        this.element.querySelectorAll("[data-work-subtask-optimistic]").forEach((node) => node.remove())
      }
      clearToasts(region)
    } else if (token) {
      // Failed: revert only the failed op's row.
      if (row) row.remove()
      showToast(region, this._subtaskErrorCopy(), "error")
    } else {
      // Tokenless (legacy W4) ack — it cannot be attributed to an op, so it
      // keeps the coarse sweep (mirror of the comments W9 legacy branch).
      this.element.querySelectorAll("[data-work-subtask-optimistic]").forEach((node) => node.remove())
      showToast(region, this._subtaskErrorCopy(), "error")
    }
  }

  // The optimistic row minted by one submit, found by iteration over the
  // stamped rows (the kit's lookup idiom — never an interpolated selector).
  _optimisticRowByOp(token) {
    const rows = this.element.querySelectorAll("[data-work-subtask-optimistic]")
    for (const node of rows) {
      if (node.getAttribute("data-work-subtask-op") === token) return node
    }
    return null
  }

  _worklogErrorCopy() {
    return this.element.getAttribute("data-work-worklog-error") || "Could not log work"
  }

  _subtaskErrorCopy() {
    return this.element.getAttribute("data-work-subtask-error") || "Could not create subtask"
  }

  _handleAttachmentAck(event) {
    const detail = event.detail || {}
    if (detail[this.subjectKeyValue] !== this.issueIdValue) return
    const region = toastRegionFor(this.element)
    if (detail.ok) {
      // The host re-renders the list with the persisted row.
      clearToasts(region)
    } else {
      showToast(region, this._attachmentErrorCopy(), "error")
    }
  }

  // Host-dispatched consumption event: ONLY here does a row leave the list.
  _handleAttachmentRemove(event) {
    const detail = event.detail || {}
    if (detail[this.subjectKeyValue] !== this.issueIdValue) return
    const attachmentId = detail.attachment_id === undefined || detail.attachment_id === null ? "" : String(detail.attachment_id)
    if (!attachmentId) return
    // Attribute-value match (never a selector built from host data).
    const rows = this.element.querySelectorAll("[data-work-attachment-id]")
    for (const candidate of rows) {
      if (candidate.getAttribute("data-work-attachment-id") === attachmentId) {
        candidate.remove()
        return
      }
    }
  }

  _attachmentErrorCopy() {
    return this.element.getAttribute("data-work-attachment-error") || "Could not upload attachment"
  }

  _emit(eventName, detail) {
    this.element.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true }))
  }
}
